import type {
  PoliticsAnswerResult,
  PoliticsCourseProgress,
  PoliticsDashboard,
  PoliticsOptionKey,
  PoliticsPracticeSource,
  PoliticsQuestionBankSummary,
  PoliticsQuestionDetail,
  PoliticsQuestionListItem,
  PoliticsQuestionOrder,
  PoliticsQuestionPrompt,
  PoliticsQuestionSectionTree,
  PoliticsQuestionSolution,
  PoliticsQuestionTypeFilter,
  PoliticsSessionItem,
  PoliticsSessionStatus,
  PoliticsSessionSummary,
  PoliticsSessionView,
} from "@preptrack/contracts";

export type {
  PoliticsAnswerResult,
  PoliticsCourseProgress,
  PoliticsDashboard,
  PoliticsOptionKey,
  PoliticsPracticeSource,
  PoliticsQuestionDetail,
  PoliticsQuestionListItem,
  PoliticsQuestionOrder,
  PoliticsQuestionPrompt,
  PoliticsQuestionSolution,
  PoliticsSessionItem,
  PoliticsQuestionTypeFilter,
  PoliticsSessionStatus,
  PoliticsSessionSummary,
};

export type PoliticsBank = PoliticsQuestionBankSummary;
export type PoliticsSection = PoliticsQuestionSectionTree;
export type PoliticsSession = PoliticsSessionView;

export interface PoliticsSessionProgress {
  answeredCount: number;
  correctCount: number;
  wrongCount: number;
  actualCount: number;
  completed: boolean;
}

export const QUESTION_TYPE_LABELS = {
  all: "全部题型",
  single_choice: "单项选择题",
  multiple_choice: "多项选择题",
} satisfies Record<PoliticsQuestionTypeFilter, string>;

export const QUESTION_ORDER_LABELS = {
  sequential: "顺序练习",
  random: "随机练习",
} satisfies Record<PoliticsQuestionOrder, string>;

export const SESSION_STATUS_LABELS = {
  active: "进行中",
  completed: "已完成",
  abandoned: "已结束",
} satisfies Record<PoliticsSessionStatus, string>;

export const PRACTICE_SOURCE_LABELS = {
  bank: "整套题库",
  section: "章节练习",
  wrong: "错题重练",
  favorites: "收藏练习",
} satisfies Record<PoliticsPracticeSource, string>;

export function formatPoliticsAccuracy(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `${Math.round(value)}%`;
}

export function politicsAccuracyPercentage(correctCount: number, answeredCount: number) {
  return answeredCount > 0 ? (correctCount / answeredCount) * 100 : null;
}

export function formatPoliticsDate(value: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Shanghai",
  }).format(new Date(value));
}

export function normalizedOptionKeys(keys: PoliticsOptionKey[]) {
  return [...new Set(keys)].sort();
}

export function politicsSessionRequestBody<T extends { clientRequestId: string }>(
  request: T,
  replaceActive: boolean,
): T & { replaceActive: boolean } {
  return { ...request, replaceActive };
}

export function politicsAnswerErrorDisposition(errorCode: string) {
  return ["answer_already_submitted", "not_current_item", "session_not_active"].includes(errorCode)
    ? "synchronize" as const
    : "retry" as const;
}
